import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ScholarshipListItem } from '../../../../types/scholarships';
import { EditIcon } from '../../../../assets/EditIcon';
import { TrashIcon } from '../../../../assets/TrashIcon';
import { restrictToVerticalAxis } from './dragModifiers';
import {
  ArchiveBoxIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CopyIcon,
  DragHandleIcon,
  RestoreIcon,
} from './icons';

export type ScholarshipRowAction = 'rename' | 'duplicate' | 'archive' | 'restore' | 'delete';

/** Which way a row moves. Indexes are into the SECTION, not the page the row is drawn on. */
export type ScholarshipMove = { from: number; to: number };

interface ScholarshipsTableProps {
  items: ScholarshipListItem[];
  // The list is split into a table per kind of board, so each one needs a name of its own.
  // Without it a screen reader announces two identical unlabelled tables.
  ariaLabel: string;
  /**
   * Where `items` starts inside its section. The table draws one page; a move is expressed
   * against the whole section, so a row can move onto the page before or after this one.
   */
  pageOffset: number;
  /** How many boards the section holds in total, so the last row knows it cannot move down. */
  sectionCount: number;
  /**
   * Absent while the order cannot be changed -- with "Show archived" on, the list carries rows
   * the API's reorder does not accept, so the whole affordance goes away rather than moving a
   * row and having the save refused.
   */
  onMove?: (move: ScholarshipMove) => void;
  onRowAction: (action: ScholarshipRowAction, scholarship: ScholarshipListItem) => void;
}

const formatLastUpdated = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

interface RowProps {
  scholarship: ScholarshipListItem;
  /** Its index in the SECTION, which is what a move is expressed in. */
  index: number;
  sectionCount: number;
  onMove?: (move: ScholarshipMove) => void;
  onRowAction: (action: ScholarshipRowAction, scholarship: ScholarshipListItem) => void;
}

const ScholarshipRow = ({
  scholarship: s,
  index,
  sectionCount,
  onMove,
  onRowAction,
}: RowProps) => {
  const sortable = useSortable({ id: s.id, disabled: !onMove });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const rowClass = `scholarships-table__row${s.isArchived ? ' scholarships-table__row--archived' : ''}`;

  return (
    <tr
      ref={setNodeRef}
      className={rowClass}
      style={
        onMove
          ? {
              transform: CSS.Transform.toString(transform),
              transition,
              opacity: isDragging ? 0.4 : undefined,
            }
          : undefined
      }
    >
      {onMove && (
        <td className="scholarships-table__cell scholarships-table__cell--handle">
          <button
            type="button"
            className="roster-drag-handle"
            aria-label={`Drag to reorder ${s.name}`}
            {...attributes}
            {...listeners}
          >
            <DragHandleIcon />
          </button>
        </td>
      )}
      <td className="scholarships-table__cell scholarships-table__cell--name">
        <Link to={`/rosters/${s.id}`} className="scholarships-table__row-link">
          {/*
            No kind badge on the row: the section heading above the table already says
            which kind these are, so a per-row label would repeat it on every line.
          */}
          <span className="scholarships-table__name">{s.name}</span>
          {s.isArchived && <span className="scholarships-table__archived-pill">Archived</span>}
        </Link>
      </td>
      <td className="scholarships-table__cell scholarships-table__cell--count">
        <Link to={`/rosters/${s.id}`} className="scholarships-table__row-link">
          {s.playerCount}
        </Link>
      </td>
      <td className="scholarships-table__cell scholarships-table__cell--updated">
        <Link to={`/rosters/${s.id}`} className="scholarships-table__row-link">
          {formatLastUpdated(s.updatedAt)}
        </Link>
      </td>
      <td className="scholarships-table__cell scholarships-table__cell--actions">
        {/*
          Move up and move down, not only a drag handle. A handle that answers to the mouse alone
          is unusable on a keyboard, and these two are also simply easier to aim at than a drag
          across a page boundary.

          `aria-disabled`, not `disabled`, on both -- the same reason ConfirmModal gives for its
          confirm button. A real `disabled` leaves the tab order on the spot, so a coach pressing
          Move up repeatedly loses the button under their focus on the very press that reaches the
          top, and focus drops to <body>. These two exist BECAUSE a drag handle fails a keyboard,
          so stranding one here breaks the thing they were added for. The handler returns early
          instead, which refuses the move just as firmly.
        */}
        {onMove && (
          <>
            <button
              type="button"
              className="scholarships-table__action-button"
              title="Move up"
              aria-label={`Move ${s.name} up`}
              aria-disabled={index === 0}
              onClick={() => {
                if (index === 0) return;
                onMove({ from: index, to: index - 1 });
              }}
            >
              <ArrowUpIcon className="scholarships-table__action-icon" />
            </button>
            <button
              type="button"
              className="scholarships-table__action-button"
              title="Move down"
              aria-label={`Move ${s.name} down`}
              aria-disabled={index >= sectionCount - 1}
              onClick={() => {
                if (index >= sectionCount - 1) return;
                onMove({ from: index, to: index + 1 });
              }}
            >
              <ArrowDownIcon className="scholarships-table__action-icon" />
            </button>
          </>
        )}
        <button
          type="button"
          className="scholarships-table__action-button"
          title="Rename"
          aria-label={`Rename ${s.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRowAction('rename', s);
          }}
        >
          <EditIcon className="scholarships-table__action-icon" />
        </button>
        <button
          type="button"
          className="scholarships-table__action-button"
          title="Duplicate"
          aria-label={`Duplicate ${s.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRowAction('duplicate', s);
          }}
        >
          <CopyIcon className="scholarships-table__action-icon" />
        </button>
        {s.isArchived ? (
          <button
            type="button"
            className="scholarships-table__action-button"
            title="Restore"
            aria-label={`Restore ${s.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onRowAction('restore', s);
            }}
          >
            <RestoreIcon className="scholarships-table__action-icon" />
          </button>
        ) : (
          <button
            type="button"
            className="scholarships-table__action-button"
            title="Archive"
            aria-label={`Archive ${s.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onRowAction('archive', s);
            }}
          >
            <ArchiveBoxIcon className="scholarships-table__action-icon" />
          </button>
        )}
        <button
          type="button"
          className="scholarships-table__action-button scholarships-table__action-button--danger"
          title="Delete"
          aria-label={`Delete ${s.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRowAction('delete', s);
          }}
        >
          <TrashIcon className="scholarships-table__action-icon" />
        </button>
      </td>
    </tr>
  );
};

export const ScholarshipsTable = ({
  items,
  ariaLabel,
  pageOffset,
  sectionCount,
  onMove,
  onRowAction,
}: ScholarshipsTableProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!onMove || !over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;
    // A drag can only land on a row that is drawn, so both ends are on this page.
    onMove({ from: pageOffset + from, to: pageOffset + to });
  };

  const table = (
    <table className="scholarships-table" aria-label={ariaLabel}>
      <thead>
        <tr>
          {onMove && (
            <th scope="col" className="scholarships-table__th scholarships-table__th--handle">
              <span className="scholarships-table__sr-only">Reorder</span>
            </th>
          )}
          <th scope="col" className="scholarships-table__th scholarships-table__th--name">Name</th>
          <th scope="col" className="scholarships-table__th scholarships-table__th--count">Players</th>
          {/*
            No aria-sort. The column said "descending" because the list used to be ordered by it,
            and it no longer is: the order is the coach's, and editing any player used to bump a
            board to the top, which is the whole reason it is.
          */}
          <th scope="col" className="scholarships-table__th scholarships-table__th--updated">
            Last updated
          </th>
          <th scope="col" className="scholarships-table__th scholarships-table__th--actions">
            <span className="scholarships-table__sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((s, i) => (
          <ScholarshipRow
            key={s.id}
            scholarship={s}
            index={pageOffset + i}
            sectionCount={sectionCount}
            onMove={onMove}
            onRowAction={onRowAction}
          />
        ))}
      </tbody>
    </table>
  );

  if (!onMove) return table;

  return (
    <DndContext
      id={`scholarships-${ariaLabel}`}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        {table}
      </SortableContext>
    </DndContext>
  );
};
